/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect } from 'react';

import { Link, useNavigate } from 'react-router-dom';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import styles from './FranchiseForm.module.scss';

import { Breadcrumb } from '@/_components/breadcrumb';
import { FareForm } from '../fare/components/fare-form/fare-form';
import { FARE_OF_TYPE } from '../fare/types';
import type { District, FranchiseType, TypeFranchiseForm } from '../fare/types';
import {
  ADDRESS_AUTOCOMPLETE_ID,
  DEFAULT_LOCATION_BRASIL,
  franchiseFormSchema,
} from '../fare/constants';
import {
  convertKilometersToMeters,
  convertMetersToKilometers,
  convertMoneyInputFormatToFloatWithNegative,
  getDirtyValues,
} from '@/Utils/FnUtils';
import { dialog } from '@/infra/dialog/Dialog';
import useGoogleMapsApi from '@/hooks/address/useGoogleMapsApi';
import type { FranchiseFareDataParams } from '../fare/api/post-franchise-fare';
import { useCreateFranchise } from '../fare/api/post-franchise-fare';
import { usePutFranchiseFare } from '../fare/api/put-franchise-fare';
import { queryClient } from '@/_config/lib/react-query/query-client';
import { GET_CITY_FARE_BY_ID } from '@/api/fares/get-city-fare-by-id';
import { useCityFareFormAccess } from '@/features/fare/hooks/use-city-fare-form-access';

interface FranchiseFormProps {
  franchise?: FranchiseType;
}

function FranchiseForm({ franchise }: FranchiseFormProps) {
  const navigate = useNavigate();
  const { accessMode } = useCityFareFormAccess();

  const breadcrumbLabel = franchise ? franchise.nome : 'Criar tarifa';

  const { mutate: createFare, isLoading: isCreating } = useCreateFranchise();
  const { mutate: updateFare, isLoading: isUpdating } = usePutFranchiseFare();

  const isLoadingSubmitFranchise = isCreating || isUpdating;

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const {
    address,
    addressLocation,
    loadingAddress,
    possibleLocal,
    onChangeAddress,
  } = useGoogleMapsApi({
    defaultLocation: franchise?.localizacao,
    autocompleteId: ADDRESS_AUTOCOMPLETE_ID,
  });

  const neighborhoodsWithFormattedValues = (franchise?.neighborhoods || []).map(
    neighborhood => ({
      ...neighborhood,
      logFee: neighborhood.logFee
        ? convertMoneyInputFormatToFloatWithNegative(
            String(neighborhood.logFee.toFixed(2)),
          )
        : 0,
      deliveryValue: neighborhood.deliveryValue
        ? convertMoneyInputFormatToFloatWithNegative(
            String(neighborhood.deliveryValue.toFixed(2)),
          )
        : 0,
      latitude: neighborhood.location?.latitude,
      longitude: neighborhood.location?.longitude,
      isActive: !!neighborhood.isActive,
    }),
  );

  const methods = useForm<TypeFranchiseForm>({
    resolver: yupResolver(franchiseFormSchema),
    context: franchise,
    defaultValues: {
      nome: franchise?.nome || '',
      uf: franchise?.uf || '',
      codigoIbge: franchise?.codigoIbge || '',
      localizacao: franchise?.localizacao
        ? {
            latitude: franchise.localizacao.latitude,
            longitude: franchise.localizacao.longitude,
          }
        : DEFAULT_LOCATION_BRASIL,
      valorEntregaBase: franchise?.valorEntregaBase
        ? convertMoneyInputFormatToFloatWithNegative(
            String(franchise.valorEntregaBase.toFixed(2)),
          )
        : 0,
      taxaEntregaBase: franchise?.taxaEntregaBase
        ? convertMoneyInputFormatToFloatWithNegative(
            String(franchise.taxaEntregaBase.toFixed(2)),
          )
        : 0,
      entregaBaseKm: franchise?.entregaBaseMts
        ? convertMetersToKilometers(franchise.entregaBaseMts)
        : 0,
      valorKmAdicional: franchise?.valorKmAdicional
        ? convertMoneyInputFormatToFloatWithNegative(
            String(franchise.valorKmAdicional.toFixed(2)),
          )
        : 0,
      taxaEntregaKmAdicional: franchise?.taxaEntregaKmAdicional
        ? convertMoneyInputFormatToFloatWithNegative(
            String(franchise.taxaEntregaKmAdicional.toFixed(2)),
          )
        : 0,
      raioMaxDistance: franchise?.raioMaxDistance
        ? convertMetersToKilometers(franchise.raioMaxDistance)
        : 0,
      qtdMaxEntregaGroup: franchise?.qtdMaxEntregaGroup || 0,
      maxDistanceDropsToGroup: franchise?.maxDistanceDropsToGroup
        ? convertMetersToKilometers(franchise.maxDistanceDropsToGroup)
        : 0,
      minDistanceCloseTo: franchise?.minDistanceCloseTo
        ? convertMetersToKilometers(franchise.minDistanceCloseTo)
        : 0,
      requiredNearPlaceToConfirm: franchise?.requiredNearPlaceToConfirm
        ? franchise?.requiredNearPlaceToConfirm
        : false,
      requiredReturnToConfirmPayMethods:
        franchise?.requiredReturnToConfirmPayMethods || [],
      fareOfType: franchise?.fareOfType || FARE_OF_TYPE.KM,
      bairros: neighborhoodsWithFormattedValues,
    },
  });

  const {
    handleSubmit,
    formState: { dirtyFields },
  } = methods;

  const handleSubmitForm: SubmitHandler<TypeFranchiseForm> = async data => {
    if (accessMode === 'readonly') {
      return;
    }

    const confirmed = await dialog.confirm(
      <div className={styles.confirmation}>
        <p>
          {`Tem certeza que deseja ${
            franchise ? 'alterar' : 'criar'
          } a tarifa?`}
        </p>
        <h1>{franchise?.nome || data?.nome}</h1>
      </div>,
      {
        isAlert: true,
      },
    );

    if (!confirmed) {
      return;
    }

    if (!franchise) {
      if (accessMode !== 'full') {
        return;
      }

      handleCreateFranchise(data);
      return;
    }

    handleEditFranchise(data);
  };

  async function handleEditFranchise(franchiseValues: TypeFranchiseForm) {
    if (accessMode === 'operational') {
      updateFare(
        {
          franchiseId: franchise?._id || '',
          fareData: {
            qtdMaxEntregaGroup: franchiseValues.qtdMaxEntregaGroup,
            requiredReturnToConfirmPayMethods:
              franchiseValues.requiredReturnToConfirmPayMethods,
            raioMaxDistance: franchiseValues.raioMaxDistance
              ? convertKilometersToMeters(franchiseValues.raioMaxDistance)
              : undefined,
            maxDistanceDropsToGroup: franchiseValues.maxDistanceDropsToGroup
              ? convertKilometersToMeters(
                  franchiseValues.maxDistanceDropsToGroup,
                )
              : undefined,
            minDistanceCloseTo: franchiseValues.minDistanceCloseTo
              ? convertKilometersToMeters(franchiseValues.minDistanceCloseTo)
              : undefined,
          },
        },
        {
          onSuccess: () => {
            navigate('/tarifas');
            queryClient.refetchQueries([
              GET_CITY_FARE_BY_ID,
              { franchiseId: franchise?._id },
            ]);
          },
          onError: (error: any) => {
            toast.warn(error.response?.data.errors.join(' - '));
          },
        },
      );
      return;
    }

    const {
      bairros,
      entregaBaseKm,
      fareOfType,
      raioMaxDistance,
      maxDistanceDropsToGroup,
      minDistanceCloseTo,
      ...newFranchiseNecessaryValues
    } = franchiseValues;

    const dirtyValues = getDirtyValues(dirtyFields, franchiseValues);

    const touchedNeighborhoods = dirtyValues.bairros || {};

    const touchedIndexes = Object.keys(touchedNeighborhoods);

    const filteredNeighborhoods = touchedIndexes.map(idx => {
      const {
        _id,
        generalCityId,
        generalNeighborhoodId,
        name,
        logFee,
        deliveryValue,
        location,
        isActive,
      } = bairros[Number(idx)];
      return {
        _id,
        generalCityId,
        generalNeighborhoodId,
        name,
        logFee,
        deliveryValue,
        location,
        isActive,
      };
    });

    const actualFareOfType = fareOfType || FARE_OF_TYPE.KM;

    const hasNeighborhoods =
      !!filteredNeighborhoods && filteredNeighborhoods?.length > 0;

    const fareData: FranchiseFareDataParams = {
      ...newFranchiseNecessaryValues,
      fareOfType: actualFareOfType
        ? (actualFareOfType as FARE_OF_TYPE)
        : undefined,
      localizacao: {
        latitude: newFranchiseNecessaryValues.localizacao?.latitude || 0,
        longitude: newFranchiseNecessaryValues.localizacao?.longitude || 0,
      },
      entregaBaseMts: entregaBaseKm
        ? convertKilometersToMeters(entregaBaseKm)
        : undefined,
      raioMaxDistance: raioMaxDistance
        ? convertKilometersToMeters(raioMaxDistance)
        : undefined,
      maxDistanceDropsToGroup: maxDistanceDropsToGroup
        ? convertKilometersToMeters(maxDistanceDropsToGroup)
        : undefined,
      minDistanceCloseTo: minDistanceCloseTo
        ? convertKilometersToMeters(minDistanceCloseTo)
        : undefined,
    };

    updateFare(
      {
        franchiseId: franchise?._id || '',
        fareData: {
          ...fareData,
          neighborhoods: hasNeighborhoods ? filteredNeighborhoods : undefined,
        },
      },
      {
        onSuccess: () => {
          navigate('/tarifas');
          queryClient.refetchQueries([
            GET_CITY_FARE_BY_ID,
            { franchiseId: franchise?._id },
          ]);
        },
        onError: (error: any) => {
          toast.warn(error.response?.data.errors.join(' - '));
        },
      },
    );
  }

  async function handleCreateFranchise(newFranchise: TypeFranchiseForm) {
    const {
      bairros,
      entregaBaseKm,
      fareOfType,
      raioMaxDistance,
      maxDistanceDropsToGroup,
      minDistanceCloseTo,
      ...newFranchiseNecessaryValues
    } = newFranchise;

    const actualNeighborhoods = bairros?.map(
      ({
        generalCityId,
        generalNeighborhoodId,
        name,
        logFee,
        deliveryValue,
        location,
        isActive,
      }: District) => ({
        name,
        generalCityId,
        generalNeighborhoodId,
        logFee,
        deliveryValue,
        location,
        isActive,
      }),
    );

    const actualFareOfType = fareOfType || FARE_OF_TYPE.KM;

    const hasNeighborhoods =
      !!actualNeighborhoods && actualNeighborhoods?.length > 0;

    const fareData: FranchiseFareDataParams = {
      ...newFranchiseNecessaryValues,
      fareOfType: actualFareOfType as FARE_OF_TYPE,
      localizacao: {
        latitude: newFranchiseNecessaryValues.localizacao?.latitude || 0,
        longitude: newFranchiseNecessaryValues.localizacao?.longitude || 0,
      },
      entregaBaseMts: entregaBaseKm
        ? convertKilometersToMeters(entregaBaseKm)
        : undefined,
      raioMaxDistance: raioMaxDistance
        ? convertKilometersToMeters(raioMaxDistance)
        : undefined,
      maxDistanceDropsToGroup: maxDistanceDropsToGroup
        ? convertKilometersToMeters(maxDistanceDropsToGroup)
        : undefined,
      minDistanceCloseTo: minDistanceCloseTo
        ? convertKilometersToMeters(minDistanceCloseTo)
        : undefined,
      neighborhoods: hasNeighborhoods ? actualNeighborhoods : undefined,
    };

    createFare(
      { fareData },
      {
        onSuccess: () => {
          navigate('/tarifas');
        },
        onError: (error: any) => {
          toast.warn(error.response?.data.errors.join(' - '));
        },
      },
    );
  }

  return (
    <div className={styles.mainContainer}>
      <Breadcrumb showBackButtonIcon={false} backButtonLabel="Tarifas">
        <Link data-actived className="text-gray" relative="path" to="">
          {breadcrumbLabel}
        </Link>
      </Breadcrumb>
      <FormProvider {...methods}>
        <FareForm
          handleSubmit={handleSubmit(handleSubmitForm)}
          addressManipulationProps={{
            onAddressChange: onChangeAddress,
            addressLocation,
            possibleLocal,
            address,
            isLoadingAddress: loadingAddress,
          }}
          isEditing={!!franchise}
          fareType="City"
          isSubmitting={isLoadingSubmitFranchise}
          importPlacesCityId={franchise?.cityId || ''}
          accessMode={accessMode}
        />
      </FormProvider>
    </div>
  );
}

export default FranchiseForm;
